import { useSyncExternalStore } from 'react';

/**
 * «Alle Artikelbilder laden»: geht den Artikelstamm durch und ruft jedes Bild einmal ab. Der Server legt es dabei
 * in den Bildspeicher (für alle Benutzer), der Service Worker auf dieses Gerät. Schon gespeicherte Bilder kosten
 * keinen Exxas-Aufruf. Der Server lädt Bilder nur in Ruhephasen und höchstens 20 pro Minute (sonst 429) – dann kurz warten und weiter,
 * Buchungen und anderes haben Vorrang. Läuft, solange die App offen ist (auch bei geschlossenem Konto-Fenster).
 */
const WARTEN_BEI_429_MS = 15_000;
const PAUSE_MS = 250;

let zustand = { laeuft: false, gesamt: 0, fertig: 0, mitBild: 0, ohneBild: 0, fehler: 0, wartet: false, ende: null, frisch: false };
let stopp = false;
const hoerer = new Set();
const setzen = (patch) => { zustand = { ...zustand, ...patch }; hoerer.forEach((h) => h()); };
const schlafen = (ms) => new Promise((r) => setTimeout(r, ms));

/** @param {boolean} frisch  true = bei Exxas neu prüfen («Alle Bilder neu holen»), sonst nur fehlende */
export function alleBilderLaden(artikel, { frisch = false } = {}) {
  if (zustand.laeuft) return;
  const urls = [...new Set((artikel ?? []).map((a) => a.bildUrl).filter((u) => u && u.startsWith('/api/')))].map((u) => (frisch ? `${u}?frisch=1` : u));
  stopp = false;
  setzen({ laeuft: true, gesamt: urls.length, fertig: 0, mitBild: 0, ohneBild: 0, fehler: 0, wartet: false, ende: null, frisch });
  (async () => {
    for (const url of urls) {
      let versuche = 0;
      for (;;) {
        if (stopp) { setzen({ laeuft: false, wartet: false, ende: 'angehalten' }); return; }
        const res = await fetch(url, { credentials: 'same-origin' }).catch(() => null);
        if (res?.status === 429 || res?.status === 503 || !res) {
          // Kontingent der Minute aufgebraucht oder kurz kein Netz → warten, dann dasselbe Bild nochmals
          if (++versuche > 40) { setzen({ fehler: zustand.fehler + 1 }); break; }
          setzen({ wartet: true });
          // Exxas sperrt bei Überlast bis zu 60 s für alle – so lange warten, wie der Server sagt
          const sek = Number(res?.headers.get('retry-after'));
          await schlafen(sek > 0 ? Math.min(sek * 1000 + 1000, 70_000) : WARTEN_BEI_429_MS);
          setzen({ wartet: false });
          continue;
        }
        if (res.status === 401) { setzen({ laeuft: false, ende: 'abgemeldet' }); return; }
        if (res.ok) setzen({ mitBild: zustand.mitBild + 1 });
        else if (res.status === 404) setzen({ ohneBild: zustand.ohneBild + 1 });
        else setzen({ fehler: zustand.fehler + 1 });
        break;
      }
      setzen({ fertig: zustand.fertig + 1 });
      await schlafen(PAUSE_MS);
    }
    setzen({ laeuft: false, wartet: false, ende: 'fertig' });
  })();
}

export function bilderLadenAnhalten() { stopp = true; }

export const useBilderLaden = () => useSyncExternalStore(
  (h) => { hoerer.add(h); return () => hoerer.delete(h); },
  () => zustand,
);
